import { PathGuide } from '@d/util'

const WAIT_TIMER = 300

/**
 * A collector that groups API requests within a time window to reduce the number of calls
 * while ensuring each caller gets their specific result.
 */
export class RetrieveCollector<T> {
  // Map of keys to their respective resolvers
  private pendingRequests = new Map<string, {
    resolve: (value: T) => void
    reject: (error: Error) => void
  }>()

  // Queue of keys waiting to be processed
  private queue = new Set<string>()

  // Timer for batch processing
  private timer: NodeJS.Timeout | null = null

  // Flag to indicate if a batch is being processed
  private processingBatch = false

  constructor(public callback: (keys: string[]) => Promise<Record<string, T>>) {}

  async get(key: string): Promise<T> {
    // Normalize the key
    key = PathGuide.from(key).toString()

    // Create a new promise for this specific key
    const promise = new Promise<T>((resolve, reject) => {
      this.pendingRequests.set(key, { resolve, reject })
    })

    // Add to the queue
    this.queue.add(key)

    // Schedule batch processing if not already scheduled
    this.scheduleBatchProcessing()

    // Return the promise for this specific key
    return promise
  }

  private scheduleBatchProcessing() {
    // Clear any existing timer
    if (this.timer) {
      clearTimeout(this.timer)
    }

    // Set new timer to process the batch
    this.timer = setTimeout(() => {
      this.processBatch()
    }, WAIT_TIMER)
  }

  private async processBatch() {
    // Avoid processing multiple batches simultaneously
    if (this.processingBatch || this.queue.size === 0) {
      return
    }

    this.processingBatch = true

    try {
      // Get all keys in the current queue
      const keysToProcess = Array.from(this.queue)

      // Clear the queue before processing
      this.queue.clear()

      // Call the callback to get all results at once
      console.log(`Processing batch with ${keysToProcess.length} keys:`, keysToProcess)
      const results = await this.callback(keysToProcess)

      // Resolve promises for each key
      for (const key of keysToProcess) {
        const resolver = this.pendingRequests.get(key)

        if (resolver) {
          const result = results[key]

          if (result !== undefined) {
            resolver.resolve(result)
          }
          else {
            console.warn(`Key "${key}" missing in results from callback. Resolving with error.`)
            resolver.resolve({ error: 'Key not found in results' } as any)
          }

          // Remove from pending requests
          this.pendingRequests.delete(key)
        }
      }
    }
    catch (error) {
      console.error('Error in RetrieveCollector batch processing:', error)

      // Reject all pending requests for this batch
      for (const key of this.queue) {
        const resolver = this.pendingRequests.get(key)
        if (resolver) {
          resolver.reject(error instanceof Error ? error : new Error(String(error)))
          this.pendingRequests.delete(key)
        }
      }
    }
    finally {
      this.processingBatch = false

      // If more items were added to the queue while processing, schedule another batch
      if (this.queue.size > 0) {
        this.scheduleBatchProcessing()
      }
    }
  }
}
